<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onBeforeUnmount, ref, watch } from 'vue'

  // External library imports

  import { Key01Icon } from '@gohighlevel/ghl-icons/24/outline'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPInputOtp from '@ui/components/CPInputOtp.vue'

  import CPText from '@ui/components/CPText.vue'

  import { useI18n } from 'vue-i18n'
  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { useCaptchaChallenge } from '../../composables/useCaptchaChallenge'

  import { LoginEvents, Screens } from '../../types/auth'

  import {
    buildSetPasswordScreenChange,
    getErrorMessage,
    isDeferredInPlacePasswordLoginResult,
    shouldShowSetPasswordAfterLoginResponse,
  } from '../../utils'

  import Recaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    title: {
      type: String,
      default: 'Login',
    },
    email: {
      type: String,
      required: false,
      default: '',
    },
    otpToken: {
      type: String,
      default: '',
    },
    password: {
      type: String,
      default: '',
    },
    username: {
      type: String,
      default: '',
    },
    locationId: {
      type: String,
      default: '',
    },
    defaultOtp: {
      type: String,
      default: null,
    },
    action: {
      type: String as PropType<'login' | 'signup'>,
      default: 'login',
    },
    /** When true, the user was not found on login — send signup OTP and continue to COMPLETE_SIGNUP after OTP entry. */
    isNewUser: {
      type: Boolean,
      default: false,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    /** Clientportal opt-in — defer session until after in-place Set Password. */
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
    showLoginWithPassword: {
      type: Boolean,
      default: true,
    },
    /**
     * Hide for magic-link OTP handoff — email is server-resolved from the token,
     * so "change email" is not a meaningful exit from that flow.
     */
    showChangeEmail: {
      type: Boolean,
      default: true,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
  }>()

  const { t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref()

  const otpCode = ref('')

  const loading = ref(false)

  const verifyDisabled = ref(false)

  const {
    showRecaptcha,
    reCaptchaToken,
    onVerify: onCaptchaVerify,
    onError,
    onExpired,
    handleRateLimit,
  } = useCaptchaChallenge(verifyDisabled)

  const isResendButtonDisabled = ref(false)

  // A 429 on resend disables it with no timer; solving the captcha is what unlocks the retry.
  const onVerify = (token: string) => {
    onCaptchaVerify(token)
    isResendButtonDisabled.value = false
  }

  const debounceTimeout = ref<NodeJS.Timeout | null>(null)

  const resendTimeout = ref<NodeJS.Timeout | null>(null)

  // Computed properties
  const isOtpValid = computed(() => {
    return otpCode.value && otpCode.value.length === 6 && !errorMessage.value
  })

  // User-defined functions
  const handleOnChange = (value: string) => {
    otpCode.value = value
    if (value.length === 6) {
      handleOnComplete({ otp: value, state: 'complete' })
    }
  }

  const handleOnCompleteInternal = async ({ otp, state }: { otp: string; state: string }) => {
    otpCode.value = otp

    if (otpCode.value?.length !== 6) return

    loading.value = true
    try {
      if (!Number(otpCode.value)) {
        errorMessage.value = t('auth.validation.secureCodeNumeric')
        loading.value = false
        return
      }
      errorMessage.value = null

      // Unknown user on login path — check the code without deleting it.
      // verifyOTPForLogin and login/otp both consume the OTP, so signup would have nothing left.
      if (props.isNewUser) {
        await useAuth().validateOtp(props.email, Number(otpCode.value), props.otpToken)
        emit('changeScreen', {
          page: Screens.COMPLETE_SIGNUP,
          email: props.email,
          otp: Number(otpCode.value),
          otpToken: props.otpToken,
          referrerUser: props.referrerUser,
        })
        showRecaptcha.value = false
        loading.value = false
        return
      }

      const signupPageUrl = window.location.href
      const result = props.password
        ? await useAuth().signUp({
            email: props.email,
            otp: Number(otpCode.value),
            redirectUrl: window.location.search.match(/redirectUrl=([^&]*)/)?.[1] || '',
            password: props.password?.trim(),
            username: props.username?.trim(),
            captchaToken: reCaptchaToken.value,
            referredBy: props.referrerUser?._id,
            signupPageUrl: signupPageUrl,
            otpToken: props.otpToken,
          })
        : await useAuth().verifyOTPForLogin(props.email, props.action, Number(otpCode.value), props.otpToken, {
            deferSessionUntilInPlacePasswordSet: props.deferSetPasswordSession,
            captchaToken: reCaptchaToken.value,
          })

      if (!props.password && isDeferredInPlacePasswordLoginResult(props.deferSetPasswordSession, result)) {
        emit('changeScreen', buildSetPasswordScreenChange(props.email, result.data))
        showRecaptcha.value = false
        loading.value = false
        return
      }

      const data = 'data' in result ? result.data : result

      if (data && 'token' in data && data.token) {
        if (shouldShowSetPasswordAfterLoginResponse(data)) {
          emit('changeScreen', buildSetPasswordScreenChange(props.email, data))
        } else {
          emit('loginCallback', {
            eventType: LoginEvents.LOGIN_SUCCESSFUL,
            ...data,
          })
        }
        loading.value = false
      }
      showRecaptcha.value = false
      loading.value = false
    } catch (error: any) {
      if (!handleRateLimit(error)) {
        console.log('error', error, error.message)
        errorMessage.value = getErrorMessage(error)
        //If user not found
        if (errorMessage.value == 'User not found') {
          emit('changeScreen', {
            email: props.email,
            page: Screens.SIGN_UP,
          })
        }
      }
      loading.value = false
    }
  }

  // Create debounced version of handleOnComplete
  const handleOnComplete = debounce(handleOnCompleteInternal, 20, debounceTimeout)

  onMounted(() => {
    console.log('props', props.otpToken)
  })

  // Cleanup timeouts on unmount
  onBeforeUnmount(() => {
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }
    if (resendTimeout.value) {
      clearTimeout(resendTimeout.value)
    }
  })

  // Simple debounce function
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number, timeoutRef: { value: NodeJS.Timeout | null }): T {
    return ((...args: any[]) => {
      if (timeoutRef.value) {
        clearTimeout(timeoutRef.value)
      }
      timeoutRef.value = setTimeout(() => func(...args), wait)
    }) as T
  }

  function isCommonUserAlreadyExists(error: unknown): boolean {
    const data = (error as { response?: { data?: { type?: string; message?: { type?: string } } } })?.response?.data
    const errorType = data?.type ?? data?.message?.type
    return errorType === 'COMMON_USER_ALREADY_EXISTS'
  }

  function finishResend(payload: {
    otpToken?: string
    isNewUser?: boolean
    action: 'login' | 'signup'
    password?: string
    username?: string
  }) {
    emit('changeScreen', {
      page: Screens.VERIFY_SECURE_CODE,
      email: props.email,
      otpToken: payload.otpToken,
      isNewUser: payload.isNewUser,
      action: payload.action,
      ...(payload.password ? { password: payload.password } : {}),
      ...(payload.username ? { username: payload.username } : {}),
      locationId: props.locationId,
    })
    isResendButtonDisabled.value = true
    if (resendTimeout.value) {
      clearTimeout(resendTimeout.value)
    }
    resendTimeout.value = setTimeout(() => (isResendButtonDisabled.value = false), 30000)
  }

  async function resendOtp() {
    if (isResendButtonDisabled.value) {
      return
    }

    // Same OTP cases as the first send:
    // - unknown login user stays on signup send
    // - existing login user stays on login send and keeps the backend isNewUser flag
    // - signup send for an existing user flips to a login OTP
    const resendAction: 'login' | 'signup' = props.isNewUser || props.action === 'signup' ? 'signup' : 'login'

    try {
      if (resendAction === 'signup') {
        try {
          const { data } = await useAuth().sendOtp({
            type: 'resendOtp',
            email: props.email,
            captchaToken: reCaptchaToken.value,
            fullName: props.username,
            action: 'signup',
          })
          finishResend({
            otpToken: data?.otpToken,
            isNewUser: props.isNewUser,
            action: 'signup',
            password: props.password?.trim(),
            username: props.username?.trim(),
          })
        } catch (error: unknown) {
          if (!isCommonUserAlreadyExists(error)) {
            throw error
          }
          const loginResponse = await useAuth().generateOTPForLogin(props.email, 'login', reCaptchaToken.value)
          finishResend({
            otpToken: loginResponse?.data?.otpToken,
            isNewUser: false,
            action: 'login',
          })
        }
        return
      }

      const { data } = await useAuth().sendOtp({
        type: 'resendOtp',
        email: props.email,
        captchaToken: reCaptchaToken.value,
        fullName: props.username,
        action: 'login',
      })
      finishResend({
        otpToken: data?.otpToken,
        isNewUser: Boolean(data?.isNewUser),
        action: 'login',
        password: props.password?.trim(),
        username: props.username?.trim(),
      })
    } catch (error: any) {
      if (handleRateLimit(error)) {
        isResendButtonDisabled.value = true
      } else {
        errorMessage.value = getErrorMessage(error)
      }
    }
  }

  // Watch effects
  watch(otpCode, () => (errorMessage.value = null))

  // Push empty state to prevent browser back navigation issues
  history.pushState({}, '', '')
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="w-full">
      <div class="mb-1 flex items-center justify-between">
        <CPText size="md" weight="medium" class="text-body">
          {{ t('auth.secureCode') }}
          <span class="text-danger">*</span>
        </CPText>
        <CPText
          :id="createElementId('secure-code', 'modal', 'resend-code-button')"
          size="sm"
          weight="semibold"
          class="text-primary"
          :class="
            isResendButtonDisabled ? 'cursor-not-allowed pointer-events-none opacity-50 select-none' : 'cursor-pointer hover:underline'
          "
          role="button"
          :tabindex="isResendButtonDisabled ? -1 : 0"
          :aria-disabled="isResendButtonDisabled"
          @click="resendOtp"
          @keydown.enter.prevent="resendOtp"
          @keydown.space.prevent="resendOtp"
        >
          {{ t('auth.resendCodeToEmail') }}
        </CPText>
      </div>

      <div class="mb-1 flex w-full items-center justify-center max-sm:min-w-0">
        <CPInputOtp
          :id="createElementId('secure-code', 'modal', 'otp-input')"
          :fields="6"
          size="sm"
          :separator-position="3"
          class="login-otp-input__fields w-full max-sm:min-w-0"
          @onComplete="handleOnComplete"
          @onChange="handleOnChange"
        />
      </div>

      <div v-if="errorMessage" :id="createElementId('secure-code', 'modal', 'error-message')" class="text-danger mb-3 w-full">
        <CPText size="sm">{{ errorMessage }}</CPText>
      </div>
      <CPText v-else size="sm" class="text-clientportal-primary mb-3">
        {{ t('auth.enterSecureCodeHint') }}
      </CPText>

      <Recaptcha
        v-if="showRecaptcha"
        :id="createElementId('secure-code', 'modal', 'recaptcha')"
        class="mb-4 flex w-full justify-center"
        @expired="onExpired"
        @verify="onVerify"
        @error="onError"
      />

      <CPButton
        :id="createElementId('secure-code', 'modal', 'verify-button')"
        class="my-4 w-full rounded-lg"
        type="primary"
        size="md"
        :loading="loading"
        :disabled="loading || verifyDisabled || !isOtpValid"
        @click="handleOnComplete({ otp: otpCode, state: 'complete' })"
      >
        {{ t('auth.verifySecureCode') }}
        <template #iconLeft>
          <Key01Icon class="w-4 h-4" />
        </template>
      </CPButton>

      <template v-if="showLoginWithPassword">
        <CPButton
          v-if="showChangeEmail"
          :id="createElementId('secure-code', 'modal', 'change-email-button')"
          class="mb-4 w-full rounded-lg"
          type="ghost"
          size="md"
          @click="emit('changeScreen', { page: Screens.LOGIN_WITH_SECURE_CODE, email: props.email })"
        >
          {{ t('auth.changeEmail') }}
        </CPButton>

        <div class="mb-4 flex w-full items-center justify-center">
          <CPDivider class="flex-1" margin-bottom="0" margin-top="0">{{ t('auth.or') }}</CPDivider>
        </div>

        <CPButton
          :id="createElementId('secure-code', 'modal', 'login-with-password-button')"
          class="w-full rounded-lg"
          type="secondary"
          variant="secondary"
          color="secondary"
          size="md"
          @click="emit('changeScreen', { page: Screens.LOGIN })"
        >
          {{ t('auth.loginWithPassword') }}
        </CPButton>
      </template>
    </div>
  </div>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 0px;
  }

  /*
   * HLInputOtp packs digit boxes to the start. In a wide host (onboarding auth
   * column) that leaves empty space on the right while Verify is full-width.
   * Spread fields across the row so the OTP group matches the button width.
   */
  :deep(.login-otp-input__fields.hr-input-otp__container),
  :deep(.login-otp-input__fields .hr-input-otp__fields) {
    width: 100%;
  }

  :deep(.login-otp-input__fields .hr-input-otp__fields) {
    display: flex;
    justify-content: space-between;
  }

  /*
   * HL sizes each cell with a fixed --n-input-width (56px for sm), so 6 cells +
   * gaps + separator overflow narrow mobile cards. Below the sm breakpoint, let
   * cells shrink below that width (never grow past it) and stay square.
   */
  @media (max-width: 639px) {
    :deep(.login-otp-input__fields .hr-input-otp__fields) {
      min-width: 0;
    }

    :deep(.login-otp-input__fields .hr-otp-field-wrapper) {
      flex: 0 1 var(--n-input-width);
      width: auto;
      min-width: 0;
      height: auto;
      aspect-ratio: 1;
    }

    :deep(.login-otp-input__fields .hr-otp-field-input) {
      width: 100%;
      min-width: 0;
      font-size: min(var(--n-input-font-size), 7vw);
    }
  }
</style>
